<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useAlert } from 'dashboard/composables';
import VisitorsAPI from 'dashboard/api/visitors';
import ConversationApi from 'dashboard/api/conversations';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const REFRESH_INTERVAL = 30000;
const ACTIVITY_REFRESH_DELAY = 3000;
const PER_PAGE = 25;

const { t } = useI18n();
const router = useRouter();

const visitors = ref([]);
const meta = ref({ online_count: 0, active_count: 0, count: 0 });
const page = ref(1);
const isLoading = ref(true);
const isRefreshing = ref(false);
const hasError = ref(false);

const totalPages = computed(() =>
  Math.max(Math.ceil((meta.value.count || 0) / PER_PAGE), 1)
);
const hasVisitors = computed(() => visitors.value.length > 0);

const deviceLabel = visitor => {
  const { browser_name: browserName, platform_name: platformName } =
    visitor.browser || {};
  return [browserName, platformName].filter(Boolean).join(' · ');
};

const pageLabel = visitor =>
  visitor.current_title || visitor.current_url || t('VISITORS.NO_PAGE');

const fetchVisitors = async ({ silent = false } = {}) => {
  if (!silent) isLoading.value = true;
  hasError.value = false;

  try {
    const { data } = await VisitorsAPI.get(page.value, PER_PAGE);
    visitors.value = data.payload || [];
    meta.value = data.meta || meta.value;
  } catch (error) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
    isRefreshing.value = false;
  }
};

const goToPage = nextPage => {
  page.value = nextPage;
  fetchVisitors();
};

const refresh = async () => {
  isRefreshing.value = true;
  await fetchVisitors({ silent: true });
};

const startConversation = async visitor => {
  try {
    const { data } = await ConversationApi.create({
      inbox_id: visitor.inbox_id,
      contact_id: visitor.id,
    });
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: data.id },
    });
  } catch (error) {
    useAlert(t('VISITORS.CHAT_ERROR'));
  }
};

const openConversation = visitor => {
  if (visitor.conversation?.id) {
    router.push({
      name: 'inbox_conversation',
      params: { conversation_id: visitor.conversation.id },
    });
    return;
  }

  if (!visitor.inbox_id) {
    useAlert(t('VISITORS.CHAT_UNAVAILABLE'));
    return;
  }

  startConversation(visitor);
};

let refreshTimer = null;
let activityTimer = null;

// Page activity arrives in bursts: coalesce them into a single refetch.
const handleVisitorActivity = () => {
  if (activityTimer) return;

  activityTimer = setTimeout(() => {
    activityTimer = null;
    fetchVisitors({ silent: true });
  }, ACTIVITY_REFRESH_DELAY);
};

onMounted(() => {
  fetchVisitors();
  refreshTimer = setInterval(
    () => fetchVisitors({ silent: true }),
    REFRESH_INTERVAL
  );
  emitter.on(BUS_EVENTS.VISITOR_ACTIVITY, handleVisitorActivity);
});

onBeforeUnmount(() => {
  clearInterval(refreshTimer);
  clearTimeout(activityTimer);
  emitter.off(BUS_EVENTS.VISITOR_ACTIVITY, handleVisitorActivity);
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header
      class="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-n-weak"
    >
      <div class="flex flex-col gap-2">
        <h1 class="text-base font-medium text-n-slate-12">
          {{ t('VISITORS.HEADER') }}
        </h1>
        <div class="flex items-center gap-2">
          <span
            class="inline-flex items-center rounded-full bg-n-teal-3 px-2.5 py-1 text-xs font-medium text-n-teal-11"
          >
            {{ t('VISITORS.ONLINE_COUNT', { count: meta.online_count }) }}
          </span>
          <span
            class="inline-flex items-center rounded-full bg-n-blue-3 px-2.5 py-1 text-xs font-medium text-n-blue-11"
          >
            {{ t('VISITORS.ACTIVE_COUNT', { count: meta.active_count }) }}
          </span>
        </div>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2"
        @click="refresh"
      >
        <Icon
          icon="i-lucide-refresh-cw"
          :class="{ 'animate-spin': isRefreshing }"
        />
        {{ t('VISITORS.REFRESH') }}
      </button>
    </header>

    <main class="flex-1 overflow-y-auto">
      <div v-if="isLoading" class="flex items-center justify-center h-full">
        <Spinner />
      </div>

      <div
        v-else-if="!hasVisitors"
        class="flex flex-col items-center justify-center h-full gap-2 px-6 text-center"
      >
        <Icon icon="i-lucide-globe" class="size-10 text-n-slate-9" />
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('VISITORS.EMPTY') }}
        </p>
        <p class="text-sm text-n-slate-11">
          {{ t('VISITORS.EMPTY_MORE_TEXT') }}
        </p>
      </div>

      <ul v-else class="divide-y divide-n-weak">
        <li
          v-for="visitor in visitors"
          :key="visitor.id"
          class="flex items-center gap-3 px-6 py-3"
        >
          <Avatar
            :name="visitor.name"
            :src="visitor.thumbnail || ''"
            :status="visitor.availability_status"
            :country-code="visitor.country_code || ''"
            :size="40"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium truncate text-n-slate-12">
              {{ visitor.name }}
            </p>
            <p class="text-sm truncate text-n-slate-11">
              {{ pageLabel(visitor) }}
            </p>
            <p
              v-if="deviceLabel(visitor)"
              class="text-xs truncate text-n-slate-10"
            >
              {{ deviceLabel(visitor) }}
            </p>
          </div>
          <button
            type="button"
            class="inline-flex items-center justify-center size-8 rounded-lg shrink-0 text-n-slate-12 hover:bg-n-alpha-2"
            :title="t('VISITORS.CHAT')"
            @click="openConversation(visitor)"
          >
            <Icon icon="i-lucide-message-square" />
          </button>
        </li>
      </ul>

      <div v-if="hasError" class="px-6 py-4 text-sm text-n-ruby-11">
        {{ t('VISITORS.ERROR') }}
      </div>
    </main>

    <footer
      v-if="totalPages > 1"
      class="flex items-center justify-between px-6 py-3 border-t border-n-weak"
    >
      <span class="text-xs text-n-slate-11">
        {{ t('VISITORS.COUNT', { count: meta.count }) }}
      </span>
      <div class="flex items-center gap-2">
        <button
          type="button"
          class="px-3 py-1 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40"
          :disabled="page === 1"
          @click="goToPage(page - 1)"
        >
          {{ t('VISITORS.PREVIOUS') }}
        </button>
        <button
          type="button"
          class="px-3 py-1 text-sm rounded-lg text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-40"
          :disabled="page >= totalPages"
          @click="goToPage(page + 1)"
        >
          {{ t('VISITORS.NEXT') }}
        </button>
      </div>
    </footer>
  </div>
</template>
